import {assert} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {StoreDependencyMap} from 'notebook/mediocre/EditorTypes';
import {ColumnDefinition, getColumnLabel} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {StructuredTableColumn} from 'notebook/structured-tables/StructuredTableConstants';
import {
  BaseTypedValueType,
  CELL_UI_PROPERTIES,
  Cells,
  PLACEHOLDER_CELL,
  RowData,
  Table,
  TableColumnType,
  TableData,
  TransferTableMetadata,
} from 'notebook/tables/TableConstants';
import TableUtil from 'notebook/tables/TableUtil';
import {
  TransferTableColumnGroup,
  TransferTableConfig,
  TransferTableModel,
  TransferTableRow,
} from 'notebook/transfer-tables/TransferTableConstants';
import {
  setCustomEvalForGridContainers,
  setCustomEvalForNonGridContainers,
} from 'notebook/transfer-tables/TransferTableContainersColumn';
import {setCustomEvalForPosition} from 'notebook/transfer-tables/TransferTablePositionColumn';
import {setCustomEvalForQuantity} from 'notebook/transfer-tables/TransferTableQuantityColumn';
import * as TransferTableUtil from 'notebook/transfer-tables/TransferTableUtil';
import {ContainerSchema} from 'samples/storables/StorableSchemaConstants';
import {omit} from 'util/omitKey';
import {StageRun} from 'workflows/WorkflowConstants';

// Only includes fields needed to generate TableData
export type TransferTableForConvertToTable = Partial<TransferTableModel> & {
  name: string;
  containerSchema: ContainerSchema;
  parentStorableIsGrid: boolean;
  columns: Array<StructuredTableColumn>;
  rows: Array<TransferTableRow>;
};

export function canChangeUnits(rows: Array<TransferTableRow>): boolean {
  const numTransferredRows = rows.filter((row) => row.isFinalized).length;
  return numTransferredRows === 0;
}

function getColumnDefinitions(
  transferTable: TransferTableForConvertToTable,
  stageRuns: Array<StageRun>
): Array<ColumnDefinition> {
  return transferTable.columns.map((column) =>
    TransferTableUtil.structuredTableColumnToColumnDefinition(
      column,
      transferTable.containerSchema,
      transferTable.parentStorableIsGrid,
      stageRuns,
      canChangeUnits(transferTable.rows)
    )
  );
}

interface TransferTableRowData extends RowData {
  isFinalized: boolean;
}

export function getRowsForTable(
  transferTable: TransferTableForConvertToTable,
  stageRuns: Array<StageRun>
): {
  rowData?: Array<TransferTableRowData>;
  tableRows: Cells;
} {
  const columnDefinitions = getColumnDefinitions(transferTable, stageRuns);

  if (transferTable.rows.length === 0) {
    return {
      tableRows: [
        columnDefinitions.map((columnDefinition) => {
          if (columnDefinition.type === TableColumnType.SAMPLE) {
            return {text: 'No samples added', locked: true, align: 'center'};
          } else {
            return {
              ...PLACEHOLDER_CELL,
              locked: true,
            };
          }
        }),
      ],
    };
  }
  return {
    rowData: transferTable.rows.map((row) => ({isFinalized: row.isFinalized})),
    tableRows: transferTable.rows.map((row, i) =>
      row.cells.map((cell, j) => {
        if (row.isFinalized) {
          return {...cell, locked: true};
        }
        const cellValue = {...cell, ...TransferTableUtil.getLockedCellProperty(columnDefinitions[j])};

        const columnDefinition = columnDefinitions[j];
        switch (columnDefinition.type) {
          case TableColumnType.POSITION: {
            if (columnDefinition.columnGroup === TransferTableColumnGroup.TARGET) {
              return setCustomEvalForPosition(columnDefinitions, cellValue, i);
            }
            return cellValue;
          }
          case TableColumnType.QUANTITY: {
            return setCustomEvalForQuantity(columnDefinitions, cellValue, i);
          }
          case TableColumnType.CONTAINERS: {
            if (columnDefinition.columnGroup === TransferTableColumnGroup.SOURCE) {
              return cellValue;
            }
            if (transferTable.parentStorableIsGrid) {
              return setCustomEvalForGridContainers(columnDefinitions, cellValue, i);
            } else {
              return setCustomEvalForNonGridContainers(columnDefinitions, cellValue, i);
            }
          }
          default:
            return cellValue;
        }
      })
    ),
  };
}

export function addEditableRowsToTable(table: TableData, newEditableRowCells: Cells): TableData {
  if ((table.metadata as TransferTableMetadata).isPlaceholderTable) {
    table = {
      ...table,
      metadata: {isPlaceholderTable: false},
      table: [],
    };
  }
  table = {
    ...table,
    table: [...table.table, ...newEditableRowCells],
    rowData: [...(table.rowData || []), ...newEditableRowCells.map(() => ({isFinalized: false}))],
  };
  return table;
}

export function convertTransferTableToTable(
  transferTable: TransferTableForConvertToTable,
  stageRuns: Array<StageRun>
): Table {
  const {rowData, tableRows} = getRowsForTable(transferTable, stageRuns);
  const columnDefinitions = getColumnDefinitions(transferTable, stageRuns);
  assert(
    transferTable.columns.length === columnDefinitions.length,
    'Expected 1 column definition per structured table column',
    {
      transferTableColumns: transferTable.columns,
      columnDefinitions,
    }
  );

  return {
    columnConstraints: columnDefinitions.map((columnDefinition, i) => ({
      editLabelDisabled: true,
      columnDefinition,
      structuredTableColumn: transferTable.columns[i],
    })),
    columnLabels: columnDefinitions.map((columnDefinition) => getColumnLabel(columnDefinition)),
    name: transferTable.name,
    rowData,
    table: tableRows,
    widths: transferTable.columns.map(({width}) => width),
    metadata: {
      isPlaceholderTable: transferTable.rows.length === 0,
    },
    // View props
    affixedColumnCount: 1,
    allowAddRows: false,
    allowNoRows: true,
    allowColumnEdits: true,
    allowRowRemoval: true,
    disallowCustomColumns: true,
    legacyFirstEditableColumnIndex: TransferTableUtil.getFirstEditableColumnIndex(columnDefinitions),
    headerRow: false,
    headerCol: false,
    lockedDims: true,
    _tableType:
      transferTable.rows.length === 0
        ? ModelTypeConstants.TRANSFER_TABLE_PLACEHOLDER
        : ModelTypeConstants.TRANSFER_TABLE,
    provenanceUuid: transferTable.provenanceUuid,
  };
}

/**
 * Fill in column widths and units for the transfer table (or transfer table config)
 *
 * @param table - the TableData that contains column changes
 * @param useCellValueAsDefaultValue - whether the values in the table cells should be stored
 *                                     as default values in the StructuredTableColumn
 */
export function convertStructuredTableColumnsForTransferTable({
  table,
  useCellValueAsDefaultValue,
}: {
  table: TableData;
  useCellValueAsDefaultValue: boolean;
}): TableData {
  if (useCellValueAsDefaultValue) {
    assert(
      table.table.length === 1,
      'The table is expected to have a single row when the values should be used as defaults'
    );
  }
  return {
    ...table,
    columnConstraints: table.columnConstraints.map((columnConstraint, i) => ({
      ...columnConstraint,
      structuredTableColumn: {
        ...columnConstraint!.structuredTableColumn!,
        width: table.widths[i],
        // @ts-expect-error - not all columns types have units
        units: table.columnConstraints[i] && table.columnConstraints[i].columnDefinition.units,
        ...(useCellValueAsDefaultValue ? {defaultValue: omit(table.table[0][i], ...CELL_UI_PROPERTIES)} : {}),
      },
    })),
  };
}

export function getUpdatedTransferTableRows(
  transferTable: TransferTableModel,
  table: TableData
): Array<TransferTableRow> {
  if ((table.metadata as TransferTableMetadata).isPlaceholderTable) {
    return [];
  }

  assert(
    table.rowData!.length === table.table.length,
    'Expected rowData to be the same length as the the table rows'
  );

  const numFinalizedRowsInOriginalTable = transferTable.rows.filter((row) => row.isFinalized).length;
  const numFinalizedRowsInUpdatedTable = table.rowData!.filter((rowData) => rowData.isFinalized).length;
  assert(
    numFinalizedRowsInUpdatedTable === numFinalizedRowsInOriginalTable,
    'Finalized rows cannot be added or deleted by a table update'
  );

  const rows = [];
  for (let i = 0; i < table.table.length; i++) {
    const tableRow = table.table[i];
    const rowData = table.rowData![i] as TransferTableRowData;

    const isFinalized = rowData.isFinalized;
    const transferOperationId = isFinalized ? transferTable.rows[i].transferOperationId : null;
    rows.push({
      isFinalized,
      transferOperationId,
      cells: tableRow.map((cell) => omit(cell, ...CELL_UI_PROPERTIES)),
    });
  }
  return rows;
}

export function mergeTableUpdateIntoTransferTable(
  transferTable: TransferTableModel,
  table: TableData
): TransferTableModel {
  table = convertStructuredTableColumnsForTransferTable({table, useCellValueAsDefaultValue: false});
  return {
    ...transferTable,
    rows: getUpdatedTransferTableRows(transferTable, table),
    columns: TableUtil.getStructuredTableColumnsFromTable(table),
    name: table.name,
  };
}

export function mergeTableUpdateIntoTransferTableConfig(
  transferTableConfig: TransferTableConfig,
  table: TableData
): TransferTableConfig {
  table = convertStructuredTableColumnsForTransferTable({table, useCellValueAsDefaultValue: true});
  return {
    ...transferTableConfig,
    columns: TableUtil.getStructuredTableColumnsFromTable(table),
    name: table.name,
  };
}

export function getStoreDependenciesForTransferTable(transferTable: TransferTableModel): StoreDependencyMap {
  const storeDependencies: StoreDependencyMap = {
    storableIds: [],
  };

  const editableRows = transferTable.rows.filter((row) => !row.isFinalized).map((row) => row.cells);
  for (const row of editableRows) {
    for (const [j, column] of transferTable.columns.entries()) {
      const {typedValue} = row[j];
      if (
        (column.columnGroup === TransferTableColumnGroup.SOURCE &&
          [TableColumnType.CONTAINERS, TableColumnType.PLATE].includes(column.columnType)) ||
        column.columnType === TableColumnType.PARENT_STORABLE
      ) {
        if (typedValue && typedValue.type === BaseTypedValueType.STORABLES) {
          storeDependencies.storableIds.push(...typedValue.storableSummaries.map(({id}) => id));
        }
      }
    }
  }

  return storeDependencies;
}
